import type { AuthSession } from './firebaseAuth';
import { API_KEY, IDENTITY } from './firebaseAuth';

/**
 * Google sign-in as a plain OpenID Connect redirect, with no SDK.
 *
 * The Firebase auth SDK was tried first and cannot live here: its redirect
 * flow delivers its result through a gapi iframe, which it loads from
 * `apis.google.com` at runtime. Bundling the SDK makes the BUILD
 * self-contained but not the PAGE, so `script-src 'self'` blocks the load
 * and the sign-in never completes. Nothing about that is worth relaxing the
 * policy for — this origin holds vault keys.
 *
 * What replaces it is the flow the SDK would have run anyway, minus the
 * iframe: navigate to Google, come back with an `id_token` in the fragment,
 * and trade it for a Firebase session over the REST endpoint already in
 * `connect-src`. A top-level navigation is not a script load, so nothing in
 * the policy has to move.
 *
 * `nonce` and `state` have to survive the round trip, so they sit in
 * sessionStorage — tab-scoped, cleared the moment they are used, and neither
 * one is a credential. Nothing else is written anywhere.
 */

/** Firebase Auth's own Google provider client (google-services.json,
 *  client_type 3). Public by construction; it authorises nothing alone. */
const GOOGLE_CLIENT_ID = '172972468834-5ck4i7mfidmacqb7mdijmr6o3eiiga4a.apps.googleusercontent.com';
const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

const NONCE_KEY = 'pondering.oidc.nonce';
const STATE_KEY = 'pondering.oidc.state';

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Where Google is told to come back to. Must match an Authorized redirect
 *  URI on the OAuth client exactly, query and fragment excluded. */
function redirectUri(): string {
  return `${window.location.origin}/connect`;
}

export function startGoogleSignIn(): void {
  const nonce = randomToken();
  const state = randomToken();
  sessionStorage.setItem(NONCE_KEY, nonce);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce,
    state,
    prompt: 'select_account',
  });
  window.location.assign(`${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`);
}

export class GoogleSignInError extends Error {}

/**
 * Null when this load is not a return from Google, which is most of them.
 * Anything that IS a return but does not check out throws rather than
 * falling through — a mismatched state is the one case worth being loud
 * about.
 */
export async function completeGoogleSignIn(): Promise<AuthSession | null> {
  const fragment = window.location.hash.replace(/^#/, '');
  if (!fragment) return null;

  const returned = new URLSearchParams(fragment);
  const idToken = returned.get('id_token');
  const error = returned.get('error');
  if (!idToken && !error) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  // Clear the fragment before anything can await on it, so a token never
  // lingers in the address bar or in history.
  window.history.replaceState(null, '', window.location.pathname);

  if (error) throw new GoogleSignInError(`Google refused the sign-in (${error}).`);
  if (!expectedState || returned.get('state') !== expectedState) {
    throw new GoogleSignInError('That sign-in did not start here.');
  }
  if (!expectedNonce || nonceOf(idToken!) !== expectedNonce) {
    throw new GoogleSignInError('That sign-in could not be matched to this tab.');
  }

  return exchangeWithFirebase(idToken!);
}

/** Reads the nonce back out of the id_token to confirm Google echoed OURS —
 *  the check that makes a replayed token useless. Signature verification is
 *  the token exchange's job, and it refuses anything Google did not sign. */
function nonceOf(idToken: string): string | null {
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replaceAll('-', '+').replaceAll('_', '/'));
    return (JSON.parse(json) as { nonce?: string }).nonce ?? null;
  } catch {
    return null;
  }
}

async function exchangeWithFirebase(googleIdToken: string): Promise<AuthSession> {
  const response = await fetch(`${IDENTITY}/accounts:signInWithIdp?key=${API_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      postBody: `id_token=${encodeURIComponent(googleIdToken)}&providerId=google.com`,
      requestUri: redirectUri(),
      returnSecureToken: true,
    }),
  });
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  if (!response.ok || !body?.['idToken']) {
    throw new GoogleSignInError('Could not sign in with that Google account.');
  }
  return {
    idToken: body['idToken'] as string,
    refreshToken: body['refreshToken'] as string,
    expiresAtMs: Date.now() + Number(body['expiresIn'] ?? 3600) * 1000,
    email: (body['email'] as string) ?? '',
    uid: body['localId'] as string,
  };
}
